/* eslint-disable no-unused-vars */
/* eslint-disable import/no-unresolved */
import React, { useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Decimal from 'decimal.js';
import { useD3 } from '../../hooks/useD3';
import d3 from '../../utils/d3Importer';
import slices from '../../state/slices/data';

const barHeight = window.innerWidth < 450 ? 5 : 7;
const blue = '#243f6f';
const dark = '#2b2b2b';
const orange = '#f06f10';
const red = '#e12827';
const chartSeperatorWidth = 60;
const halfChartSeperatorWidth = chartSeperatorWidth / 2;
// const indicatorBarColour = orange;

export default function Content() {
  const dispatch = useDispatch();
  const curData = useSelector((state) => state.data.curData);
  const earnLess = useSelector((state) => state.data.earnLess);
  const earnMore = useSelector((state) => state.data.earnMore);
  const incomeSelectActive = useSelector(
    (state) => state.data.incomeSelectActive,
  );
  const incomeLevel = useSelector((state) => state.data.incomeLevel);
  const timeframe = useSelector((state) => state.data.timeframe);

  const { setIncome } = slices.actions;

  const stageWidth = window.innerWidth > 630 ? 630 : window.innerWidth;
  const margin = { top: 70, right: 70, bottom: 30, left: 70 };
  const height = curData.length * barHeight + margin.top + margin.bottom;

  function roundto2dp(num) {
    return Number(new Decimal(num).toFixed(2));
  }

  function roundto1dp(num) {
    return Number(new Decimal(num).toFixed(1));
  }

  const ref = useD3((svg) => {
    if (curData.length > 0) {
      // reset
      d3.select('.area-chart-left').html('');
      d3.select('.area-chart-right').html('');
      d3.select('.x-axis').html('');
      d3.select('.x-axis-L').html('');
      d3.select('.y-axis').html('');
      d3.select('.center-line').html('');
      d3.select('.indicator-bar').html('');
      d3.select('.invis-area').html('');
      d3.select('.text-hint').html('');
      //
      // Set up the SVG
      const chartWidth = stageWidth - margin.left - margin.right;
      const chartHeight = height - margin.top - margin.bottom;

      // scales
      const x = d3
        .scaleLinear()
        .domain([0, 100])
        .range([0, chartWidth / 2 - halfChartSeperatorWidth]);

      const xR = d3
        .scaleLinear()
        .domain([0, 100])
        .range([0, -(chartWidth / 2 - halfChartSeperatorWidth)]);

      const y = d3
        .scaleBand()
        .domain(curData.map((d) => d.iID))
        .range([0, curData.length * barHeight])
        .paddingInner(0)
        .paddingOuter(0);

      const yAxis = (g) => {
        g.call(d3.axisLeft(y).tickSize(-chartWidth))
          .call(() => g.select('.domain').remove())
          .call(() => {
            g.selectAll('line').attr('stroke-dasharray', (d, i) => {
              return '2,4';
            });
            g.selectAll('line').style('opacity', (d, i) => {
              // remove opacity for every 5th tick line
              return i % 10 === 0 ? 1 : 0;
            });
          });
      };

      const xAxisTopRight = (g) => {
        g.attr(
          'transform',
          `translate(${chartWidth / 2 + halfChartSeperatorWidth}, 0)`,
        ).call(
          d3
            .axisTop(x)
            .ticks(2)
            .tickFormat((d) => `${d}%`),
        );
      };

      const xAxisTopLeft = (g) => {
        g.attr(
          'transform',
          `translate(${chartWidth / 2 - halfChartSeperatorWidth}, 0)`,
        ).call(
          d3
            .axisTop(xR)
            .ticks(2)
            .tickFormat((d) => `${d}%`),
        );
      };

      // areas
      const areaLeft = d3
        .area()
        .x0(0)
        .x1((d) => -x(d))
        .y((d, i) => i * barHeight + barHeight / 2)
        .curve(d3.curveMonotoneX);

      const areaRight = d3
        .area()
        .x0(0)
        .x1((d) => x(d))
        .y((d, i) => i * barHeight + barHeight / 2)
        .curve(d3.curveMonotoneX);

      // draw axes
      svg
        .select('.chart')
        .attr('transform', `translate(${margin.left}, ${margin.top})`);

      svg.select('.y-axis').call(yAxis);
      svg.select('.x-axis').call(xAxisTopRight);
      svg.select('.x-axis-L').call(xAxisTopLeft);

      // patterns
      const defs = svg
        .append('defs')
        .attr('class', 'defs')
        .append('pattern')
        .attr('id', 'pattern-orange')
        .attr('width', 800)
        .attr('height', 536)
        .attr('patternUnits', 'userSpaceOnUse')
        .append('image')
        .attr('xlink:href', 'images/textureOrange.jpg');

      const defs2 = svg
        .select('.defs')
        .append('pattern')
        .attr('id', 'pattern-red')
        .attr('width', 800)
        .attr('height', 536)
        .attr('patternUnits', 'userSpaceOnUse')
        .append('image')
        .attr('xlink:href', 'images/textureRed.jpg');

      // Arrows
      const defs3 = svg
        .select('.defs')
        .append('marker')
        .attr('id', 'arrow')
        .attr('viewBox', '0 -5 10 10')
        .attr('refX', 5)
        .attr('refY', 0)
        .attr('markerWidth', 6)
        .attr('markerHeight', 6)
        .attr('orient', 'auto-start-reverse')
        .append('path')
        .attr('d', 'M0,-5L10,0L0,5')
        .attr('fill', red);

      const defs4 = svg
        .select('.defs')
        .append('marker')
        .attr('id', 'arrowOrange')
        .attr('viewBox', '0 -5 10 10')
        .attr('refX', 5)
        .attr('refY', 0)
        .attr('markerWidth', 6)
        .attr('markerHeight', 6)
        .attr('orient', 'auto-start-reverse')
        .append('path')
        .attr('d', 'M0,-5L10,0L0,5')
        .attr('fill', orange);

      svg
        .select('.center-line')
        .append('line')
        .attr('x1', chartWidth / 2 + halfChartSeperatorWidth)
        .attr('x2', chartWidth)
        .attr('y1', -30) // Adjust the position based on your layout
        .attr('y2', -30)
        .attr('stroke', red)
        .attr('marker-end', 'url(#arrow)');

      svg
        .select('.center-line')
        .append('line')
        .attr('x1', chartWidth / 2 - halfChartSeperatorWidth)
        .attr('x2', 0)
        .attr('y1', -30) // Adjust the position based on your layout
        .attr('y2', -30)
        .attr('stroke', orange)
        .attr('marker-end', 'url(#arrow)');

      svg
        .select('.center-line')
        .append('text')
        .attr('class', 'arrow-hint-text')
        .attr('x', chartWidth / 2 + halfChartSeperatorWidth)
        .attr('y', -40) // Adjust the position based on your layout
        .text('Per cent people earning more')
        .style('text-anchor', 'start')
        .style('fill', red);

      svg
        .select('.center-line')
        .append('text')
        .attr('class', 'arrow-hint-text')
        .attr('x', chartWidth / 2 - halfChartSeperatorWidth)
        .attr('y', -40) // Adjust the position based on your layout
        .text('Per cent people earning less')
        .style('text-anchor', 'end')
        .style('fill', orange);

      // draw center line
      svg
        .select('.center-line')
        .append('line')
        .attr('x1', chartWidth / 2 - halfChartSeperatorWidth)
        .attr('x2', chartWidth / 2 - halfChartSeperatorWidth)
        .attr('y1', barHeight / 2)
        .attr('y2', chartHeight - barHeight / 2)
        .attr('stroke', '#000')
        .attr('stroke-width', 0.5);

      svg
        .select('.center-line')
        .append('line')
        .attr('x1', chartWidth / 2 + halfChartSeperatorWidth)
        .attr('x2', chartWidth / 2 + halfChartSeperatorWidth)
        .attr('y1', barHeight / 2)
        .attr('y2', chartHeight - barHeight / 2)
        .attr('stroke', '#000')
        .attr('stroke-width', 0.5);

      // draw area charts
      svg
        .select('.area-chart-left')
        .attr(
          'transform',
          `translate(${chartWidth / 2 - halfChartSeperatorWidth}, 0)`,
        )
        .attr('class', 'area-chart-left')
        .append('path')
        .datum(earnLess)
        .attr('d', areaLeft)
        .style('fill', 'url(#pattern-orange)');

      svg
        .select('.area-chart-right')
        .attr(
          'transform',
          `translate(${chartWidth / 2 + halfChartSeperatorWidth}, 0)`,
        )
        .attr('class', 'area-chart-right')
        .append('path')
        .datum(earnMore)
        .attr('d', areaRight)
        .style('fill', 'url(#pattern-red)');

      // add text hints

      svg
        .select('.text-hint')
        .append('text')
        .attr('x', chartWidth / 2 + 10 + halfChartSeperatorWidth)
        .attr('y', barHeight * 2 + 10)
        .attr('class', 'text-hints-earn')
        .text(() => {
          return window.innerWidth < 450 ? 'Earn more' : 'Earning more';
        })
        .style('text-anchor', 'start');

      svg
        .select('.text-hint')
        .append('text')
        .attr('x', chartWidth / 2 - 10 - halfChartSeperatorWidth)
        .attr('y', barHeight * curData.length - 20)
        .attr('class', 'text-hints-earn')
        .text(() => {
          return window.innerWidth < 450 ? 'Earn less' : 'Earning less';
        })
        .style('text-anchor', 'end');

      svg
        .select('.text-hint')
        .append('text')
        .attr(
          'transform',
          `translate(${-margin.left / 2},${chartHeight * 0.25}) rotate(-90)`,
        )
        .attr('dominant-baseline', 'middle')
        .attr('text-anchor', 'middle')
        .attr('class', 'text-hint-y-axis')
        .text('Lower income levels');

      svg
        .select('.text-hint')
        .append('text')
        .attr(
          'transform',
          `translate(${chartWidth + margin.right / 2},${
            chartHeight * 0.75
          }) rotate(90)`,
        )
        .attr('dominant-baseline', 'middle')
        .attr('text-anchor', 'middle')
        .attr('class', 'text-hint-y-axis')
        .text('Higher income levels');

      // draw indicator bar if income has been selected - 2 small circles with a bar between
      if (incomeSelectActive) {
        // get data points for the indicator bar
        const indicatorIndex = curData.findIndex((d) => d.iID === incomeLevel);
        const leftX = x(earnLess[indicatorIndex]);
        const rightX = x(earnMore[indicatorIndex]);
        const yVal = indicatorIndex * barHeight + barHeight / 2;
        const earnLessValue = roundto1dp(earnLess[indicatorIndex]);
        const earnMoreValue = roundto1dp(earnMore[indicatorIndex]);

        const indicatorBar = svg
          .select('.indicator-bar')
          .attr('transform', `translate(${chartWidth / 2}, 0)`);

        indicatorBar
          .append('line')
          .attr('x1', -leftX - halfChartSeperatorWidth)
          .attr('x2', -halfChartSeperatorWidth)
          .attr('y1', yVal)
          .attr('y2', yVal)
          .attr('stroke', orange)
          .attr('stroke-width', 2);

        indicatorBar
          .append('line')
          .attr('x1', halfChartSeperatorWidth)
          .attr('x2', rightX + halfChartSeperatorWidth)
          .attr('y1', yVal)
          .attr('y2', yVal)
          .attr('stroke', red)
          .attr('stroke-width', 2);

        indicatorBar
          .append('circle')
          .attr('cx', -leftX - halfChartSeperatorWidth)
          .attr('cy', yVal)
          .attr('r', 5)
          .attr('fill', orange);

        indicatorBar
          .append('circle')
          .attr('cx', rightX + halfChartSeperatorWidth)
          .attr('cy', yVal)
          .attr('r', 5)
          .attr('fill', red);

        // add text organge percentage
        indicatorBar
          .append('text')
          .attr('class', 'text-indicator')
          .attr('x', -leftX - halfChartSeperatorWidth)
          .attr('y', yVal)
          .attr('dx', -10)
          .attr('text-anchor', 'end')
          .text(`${earnLessValue}%`)
          .style('fill', orange);

        // add text red percentage
        indicatorBar
          .append('text')
          .attr('class', 'text-indicator')
          .attr('x', rightX + halfChartSeperatorWidth)
          .attr('y', yVal)
          .attr('dx', 10)
          .attr('text-anchor', 'start')
          .text(`${earnMoreValue}%`)
          .style('fill', red);

        indicatorBar
          .append('text')
          .attr('class', 'text-indicator')
          .attr('x', 0)
          .attr('y', yVal)
          .attr('dx', 0)
          .attr('text-anchor', 'middle')
          .text(`${roundto1dp(curData[indicatorIndex].diff)}%`)
          .style('fill', blue);

        indicatorBar
          .append('text')
          .attr('x', 0)
          .attr('y', yVal - 18)
          .attr('class', 'you')
          .attr('text-anchor', 'middle')
          .style('fill', blue)
          .text('YOU');

        /*
        indicatorBar
          .append('image')
          .attr('xlink:href', 'images/headIcon.svg')
          .attr('width', 20)
          .attr('height', 20)
          .attr('x', -10)
          .attr('y', yVal - 58); */
      }

      // add invisible area for mouseover
      svg
        .select('.invis-area')
        .selectAll('rect')
        .data(curData)
        .join('rect')
        .attr('x', 0)
        .attr('y', (d, i) => i * barHeight)
        .attr('width', chartWidth)
        .attr('height', barHeight)
        .attr('fill', 'rgba(0,0,0,100)')
        .attr('opacity', 0)
        .on('mousedown', (event, d, i) => {
          console.log('clicked iID:', d.iID, 'datum:', d);
          dispatch(setIncome(d.iID));
        });
    }
  });

  const style = {
    width: stageWidth,
    height,
    marginRight: '0px',
    marginLeft: '0px',
  };

  const wrapStyle = {
    display: curData.length > 0 ? 'block' : 'none',
  };

  function getChartHeading() {
    if (incomeSelectActive) {
      // get data points for the indicator bar
      const indicatorIndex = curData.findIndex((d) => d.iID === incomeLevel);
      if (timeframe === 'annual') {
        return `Relative income position for ${curData[indicatorIndex].annual}${curData[indicatorIndex].dataStatus}`;
      }
      return `Relative income position for ${curData[indicatorIndex].weekly}${curData[indicatorIndex].dataStatus}`;
    }
    return 'Relative income position';
  }

  return (
    <section className="chart-wrap" style={wrapStyle}>
      <h3>{getChartHeading()}</h3>
      <svg ref={ref} style={style}>
        <g className="chart">
          <g className="area-chart-left" />
          <g className="area-chart-right" />
          <g className="x-axis" />
          <g className="x-axis-L" />
          <g className="y-axis" />
          <g className="center-line" />
          <g className="text-hint" />
          <g className="indicator-bar" />
          <g className="invis-area" />
        </g>
      </svg>
    </section>
  );
}
